'use client';

import type React from 'react';
import { useEffect, useRef } from 'react';
import { ChatMessage } from '@/components/chat/chat-message';
import { TUTORIAL_CHAT_MESSAGES } from './tutorial-chat-messages';

interface TutorialChatBodyProps {
  className?: string;
  /** When true the body fills its parent (right-rail docked sidebar);
   *  when false it caps height the way the floating panel does. */
  docked?: boolean;
}

/**
 * Read-only chat body rendered inside the lobby's `ChatPanel` when a
 * `TutorialContext` is active. Mirrors the SDK chat layout (scrollable
 * message list + faux input row) so the tutorial's `holder-chat`
 * spotlight lands on something that *looks* like a real chat panel.
 *
 * The SDK chat is bypassed entirely here — its verify / fetch /
 * send-message paths assume a connected, holder-gated wallet, which
 * the tutorial wallet shim deliberately is not. Rendering the static
 * backlog through `<ChatMessage>` keeps the visual fidelity (avatars,
 * threaded replies, holder chips) without any of the network plumbing.
 *
 * The faux input row is non-interactive — the tutorial input guard
 * already blocks all chat-panel events during the holder-chat step,
 * but the disabled placeholder makes that intent visible to the user
 * (no flashing caret, "Tutorial mode" hint copy).
 */
export function TutorialChatBody({ className, docked = false }: TutorialChatBodyProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scroll the static backlog to the bottom on mount + on layout
  // shifts. The chat panel auto-opens once the tutorial user owns a
  // seat, so the very first paint should land on the most recent
  // message — without `scrollIntoView` the scroller stays at the
  // top and the user has to scroll down to see live conversation.
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ block: 'end' });
    }
  }, []);

  // Swallow clicks on the underlying `ChatMessage` rows. Each row
  // wraps its avatar in a `<button>` that opens the global profile
  // modal via `useProfileModalOptional` (the modal context is
  // mounted at the app root so the lookup succeeds in tutorial
  // mode too). A click would yank a full-screen modal up over the
  // tutorial dialogue mid-step. The capture-phase handler runs
  // before React's bubble phase reaches those buttons, so we kill
  // both the default action and propagation before any handler
  // fires. Scrolling the message list still works — wheel + touch
  // gestures don't surface as `click` events, and the tutorial
  // input guard already allows pointer/wheel events on the
  // highlight target.
  const swallowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className={[
        'flex flex-col bg-transparent text-[12px] text-amber-100/90',
        '[&_.text-muted-foreground]:text-amber-200/55',
        docked ? 'flex-1 min-h-0 overflow-hidden overscroll-y-contain' : 'h-[340px]',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-tutorial-chat-body
    >
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain [overscroll-behavior-y:contain]"
      >
        <div className="py-2 space-y-0.5" onClickCapture={swallowClick}>
          {TUTORIAL_CHAT_MESSAGES.map((msg) => (
            <ChatMessage
              key={msg.id}
              msg={msg}
              isOwn={false}
            />
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Faux input row. Non-interactive — the tutorial input guard
          rejects events targeted here during the holder-chat step
          anyway, the disabled visual is just so the panel doesn't
          look truncated. */}
      <div
        className="flex items-center gap-2 border-t border-amber-500/15 bg-zinc-950/55 px-3 py-2"
        aria-disabled
      >
        <div className="flex-1 rounded-md border border-amber-500/15 bg-black/40 px-3 py-2 text-[11px] text-amber-200/55 select-none">
          Tutorial mode — chat is read-only
        </div>
      </div>
    </div>
  );
}
